/* Product page: gallery (stage, thumbnails, full-screen zoom), variant picker / set
   configurator (deltas computed from product.variants), live price, URL and
   "Im Set enthalten" summary. */
(() => {
  const ik = (window.ikTheme = window.ikTheme || {});
  const strings = () => ik.strings || {};

  /* --------------------------------------------------------------- gallery */

  /* Stage (scroll-snap track) with arrows, counter and thumbnails, plus a full-screen
     view: click/tap toggles 2.5× zoom at the pointer, mouse drag or touch scroll pans,
     horizontal swipe or arrow keys change the image. */
  class IkGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.thumbStrip = this.querySelector('[data-gallery-thumbs]');
      this.counter = this.querySelector('[data-gallery-counter]');
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.current = 0;

      if (this.items.length > 1) {
        this.track.addEventListener('scroll', ik.debounce(() => this.syncFromScroll(), 80), { passive: true });
        this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.select(this.current - 1));
        this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.select(this.current + 1));
        this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.select(index)));
      }

      this.track.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-gallery-open]');
        if (!opener || !this.lightbox) return;
        this.openLightbox(this.items.indexOf(opener.closest('[data-gallery-item]')));
      });

      document.addEventListener('ik:variant:change', (event) => {
        const media = event.detail && event.detail.variant && event.detail.variant.featured_media;
        if (!media) return;
        const index = this.items.findIndex((item) => String(item.dataset.mediaId) === String(media.id));
        if (index >= 0) this.select(index);
      });
    }

    select(index, behavior = 'smooth') {
      const count = this.items.length;
      if (!count) return;
      const target = ((index % count) + count) % count;
      this.track.scrollTo({ left: this.items[target].offsetLeft, behavior });
      this.update(target);
    }

    syncFromScroll() {
      const width = this.track.clientWidth || 1;
      const index = Math.min(this.items.length - 1, Math.max(0, Math.round(this.track.scrollLeft / width)));
      this.update(index);
    }

    update(index) {
      this.current = index;
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.items.length}`;
      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      const thumb = this.thumbs[index];
      if (thumb && this.thumbStrip && this.thumbStrip.scrollWidth > this.thumbStrip.clientWidth) {
        const left = thumb.offsetLeft - (this.thumbStrip.clientWidth - thumb.offsetWidth) / 2;
        this.thumbStrip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }

    /* ------------------------------------------------------ full-screen view */

    openLightbox(itemIndex) {
      this.imageItems = this.items.filter((item) => item.querySelector('[data-gallery-open]'));
      if (!this.imageItems.length) return;
      this.bindLightbox();
      this.lbIndex = Math.max(0, this.imageItems.indexOf(this.items[itemIndex]));
      this.lightbox.showModal();
      ik.lockScroll(true);
      this.showLightboxImage();
    }

    bindLightbox() {
      if (this.lightboxBound) return;
      this.lightboxBound = true;
      const lb = this.lightbox;
      this.viewport = lb.querySelector('[data-lightbox-viewport]');
      this.lbImg = lb.querySelector('[data-lightbox-img]');
      this.lbCounter = lb.querySelector('[data-lightbox-counter]');
      this.zoomBtn = lb.querySelector('[data-lightbox-zoom]');

      lb.querySelector('[data-lightbox-close]').addEventListener('click', () => lb.close());
      lb.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.stepLightbox(-1));
      lb.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.stepLightbox(1));
      this.zoomBtn.addEventListener('click', () => this.toggleZoom());
      lb.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.stepLightbox(-1);
        if (event.key === 'ArrowRight') this.stepLightbox(1);
      });
      lb.addEventListener('close', () => {
        ik.lockScroll(false);
        this.resetZoom();
        const item = this.imageItems[this.lbIndex];
        if (item) this.select(this.items.indexOf(item), 'auto');
      });

      const vp = this.viewport;
      vp.addEventListener('pointerdown', (event) => {
        this.drag = { x: event.clientX, y: event.clientY, left: vp.scrollLeft, top: vp.scrollTop, type: event.pointerType, moved: false };
        if (this.zoomed && event.pointerType === 'mouse') {
          vp.setPointerCapture(event.pointerId);
          vp.classList.add('is-dragging');
        }
      });
      vp.addEventListener('pointermove', (event) => {
        if (!this.drag) return;
        const dx = event.clientX - this.drag.x;
        const dy = event.clientY - this.drag.y;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) this.drag.moved = true;
        if (this.zoomed && this.drag.type === 'mouse') {
          vp.scrollLeft = this.drag.left - dx;
          vp.scrollTop = this.drag.top - dy;
        }
      });
      const endDrag = (event) => {
        const drag = this.drag;
        this.drag = null;
        vp.classList.remove('is-dragging');
        if (!drag) return;
        this.ignoreClick = drag.moved;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!this.zoomed && drag.type !== 'mouse' && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.stepLightbox(dx < 0 ? 1 : -1);
        }
      };
      vp.addEventListener('pointerup', endDrag);
      vp.addEventListener('pointercancel', () => {
        this.drag = null;
        vp.classList.remove('is-dragging');
      });
      vp.addEventListener('click', (event) => {
        if (this.ignoreClick) {
          this.ignoreClick = false;
          return;
        }
        if (event.target === this.lbImg || this.zoomed) {
          this.toggleZoom(event.clientX, event.clientY);
        } else {
          lb.close();
        }
      });
    }

    stepLightbox(delta) {
      const count = this.imageItems.length;
      if (count < 2) return;
      this.lbIndex = (((this.lbIndex + delta) % count) + count) % count;
      this.showLightboxImage();
    }

    showLightboxImage() {
      const opener = this.imageItems[this.lbIndex].querySelector('[data-gallery-open]');
      this.resetZoom();
      this.lbImg.removeAttribute('srcset');
      this.lbImg.src = opener.dataset.full;
      this.lbImg.srcset = opener.dataset.fullSrcset || '';
      this.lbImg.sizes = '100vw';
      this.lbImg.alt = opener.dataset.alt || '';
      if (opener.dataset.width && opener.dataset.height) {
        this.lbImg.width = Number(opener.dataset.width);
        this.lbImg.height = Number(opener.dataset.height);
      }
      if (this.lbCounter) this.lbCounter.textContent = `${this.lbIndex + 1} / ${this.imageItems.length}`;
    }

    toggleZoom(clientX, clientY) {
      if (this.zoomed) {
        this.resetZoom();
        return;
      }
      const img = this.lbImg;
      const vp = this.viewport;
      const rect = img.getBoundingClientRect();
      if (!rect.width) return;
      const rx = clientX === undefined ? 0.5 : Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const ry = clientY === undefined ? 0.5 : Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
      const width = rect.width * 2.5;
      const height = rect.height * 2.5;
      this.zoomed = true;
      this.lightbox.classList.add('is-zoomed');
      img.style.width = `${width}px`;
      img.style.height = `${height}px`;
      img.style.marginLeft = width < vp.clientWidth ? `${(vp.clientWidth - width) / 2}px` : '0';
      img.style.marginTop = height < vp.clientHeight ? `${(vp.clientHeight - height) / 2}px` : '0';
      vp.scrollLeft = rx * width - vp.clientWidth / 2;
      vp.scrollTop = ry * height - vp.clientHeight / 2;
      this.zoomBtn.setAttribute('aria-pressed', 'true');
      this.zoomBtn.setAttribute('aria-label', this.zoomBtn.dataset.labelOut);
    }

    resetZoom() {
      if (!this.lbImg) return;
      this.zoomed = false;
      this.lightbox.classList.remove('is-zoomed');
      ['width', 'height', 'marginLeft', 'marginTop'].forEach((prop) => {
        this.lbImg.style[prop] = '';
      });
      this.viewport.scrollLeft = 0;
      this.viewport.scrollTop = 0;
      this.zoomBtn.setAttribute('aria-pressed', 'false');
      this.zoomBtn.setAttribute('aria-label', this.zoomBtn.dataset.labelIn);
    }
  }

  /* -------------------------------------------------------- variant picker */

  class IkVariantPicker extends HTMLElement {
    connectedCallback() {
      const variantsJson = this.querySelector('[data-variants]');
      if (!variantsJson) return;
      this.variants = JSON.parse(variantsJson.textContent);
      const config = this.querySelector('[data-set-config]');
      this.setConfig = config ? JSON.parse(config.textContent) : null;
      this.priceMode = this.dataset.priceMode || 'delta';
      this.formId = this.dataset.form;
      this.section = this.closest('.shopify-section') || document;
      this.status = this.querySelector('[data-variant-status]');
      this.groups = Array.from(this.querySelectorAll('fieldset'));

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-input]')) this.update(false);
      });
      this.update(true);
    }

    get selectedOptions() {
      return this.groups.map((group) => {
        const checked = group.querySelector('[data-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find((variant) => options.every((value, index) => variant.options[index] === value));
    }

    update(initial) {
      const options = this.selectedOptions;
      const variant = this.findVariant(options) || null;
      this.current = variant;
      this.updateOptions(options, variant);
      this.updatePrice(variant);
      this.updateForm(variant);
      this.updateSummary(options);
      this.updateDelivery(variant);
      if (!initial && variant) {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      }
      if (this.status && variant) this.status.textContent = variant.title;
      this.dispatchEvent(
        new CustomEvent('ik:variant:change', { bubbles: true, detail: { variant, options, deltas: this.lastDeltas } })
      );
      if (!initial) this.refreshDependent(variant);
    }

    /* Delivery window per variant (metafield custom.lieferzeit, rendered into
       [data-delivery-map] by Liquid so no request is needed). */
    updateDelivery(variant) {
      const mapEl = this.section.querySelector('[data-delivery-map]');
      if (!mapEl || !variant) return;
      if (!this.deliveryMap) {
        try {
          this.deliveryMap = JSON.parse(mapEl.textContent);
        } catch (error) {
          this.deliveryMap = {};
        }
      }
      const entry = this.deliveryMap[String(variant.id)] || {};
      const box = this.section.querySelector('[data-delivery-box]');
      if (box) {
        const content = box.querySelector('[data-delivery-content]');
        const has = !!entry.t;
        box.classList.toggle('delivery-box--fallback', !has);
        if (content) {
          if (has) content.innerHTML = `<b>${this.escape(entry.t)}</b> · ${this.escape(box.dataset.free || '')}`;
          else content.textContent = box.dataset.fallback || '';
        }
      }
      const sticky = this.section.querySelector('[data-sticky-delivery]');
      if (sticky) {
        sticky.textContent = entry.s || '';
        sticky.hidden = !entry.s;
      }
    }

    /* Blocks whose content comes from variant metafields (Spezifikationen,
       Matratzengröße …) are re-rendered through the Section Rendering API. */
    refreshDependent(variant) {
      const targets = Array.from(this.section.querySelectorAll('[data-variant-dependent]'));
      if (!targets.length || !variant || !this.dataset.section) return;
      if (this.refreshController) this.refreshController.abort();
      this.refreshController = new AbortController();
      const url = `${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.section}`;
      fetch(url, { signal: this.refreshController.signal })
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          targets.forEach((target) => {
            const key = target.dataset.variantDependent;
            const fresh = doc.querySelector(`[data-variant-dependent="${key}"]`);
            if (fresh) {
              target.replaceWith(fresh);
            } else {
              target.hidden = true;
            }
          });
        })
        .catch(() => {});
    }

    formatDelta(delta) {
      if (delta === 0) return `+ ${ik.formatMoney(0)}`;
      return `${delta > 0 ? '+' : '−'} ${ik.formatMoney(Math.abs(delta))}`;
    }

    updateOptions(options, current) {
      this.lastDeltas = {};
      this.groups.forEach((group, groupIndex) => {
        const label = group.querySelector('[data-selected-label]');
        if (label) label.textContent = options[groupIndex] || '';
        group.querySelectorAll('[data-option-value]').forEach((card) => {
          const input = card.querySelector('[data-option-input]');
          const combo = options.slice();
          combo[groupIndex] = input.value;
          const variant = this.findVariant(combo);
          const available = !!(variant && variant.available);
          const selected = input.checked;
          let text;
          let delta = null;
          if (!variant) text = strings().unavailable || '–';
          else if (!available) text = strings().soldOut || '–';
          else if (this.priceMode === 'absolute') text = ik.formatMoney(variant.price);
          else if (selected) text = strings().selected || '';
          else {
            delta = variant.price - (current ? current.price : variant.price);
            text = this.formatDelta(delta);
          }
          if (variant && available && !selected) {
            delta = variant.price - (current ? current.price : variant.price);
          }
          this.lastDeltas[`${groupIndex}:${input.value}`] = { delta, available, selected, text };
          const priceEl = card.querySelector('[data-option-delta]');
          if (priceEl) priceEl.textContent = text;
          card.classList.toggle('is-selected', selected);
          card.classList.toggle('is-unavailable', !available);
        });
      });
    }

    updatePrice(variant) {
      const priceEl = this.section.querySelector('[data-price]');
      const compareEl = this.section.querySelector('[data-compare-price]');
      if (!variant) return;
      if (priceEl) priceEl.textContent = ik.formatMoney(variant.price);
      if (compareEl) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        compareEl.hidden = !onSale;
        if (onSale) compareEl.textContent = ik.formatMoney(variant.compare_at_price);
      }
    }

    updateForm(variant) {
      const form = this.formId ? document.getElementById(this.formId) : null;
      const available = !!(variant && variant.available);
      if (form) {
        const idInput = form.querySelector('[data-variant-id]');
        if (idInput && variant) {
          idInput.value = variant.id;
          idInput.disabled = !available;
        }
        form.dataset.currentPrice = variant ? variant.price : 0;
        const soldOut = form.querySelector('[data-soldout]');
        if (soldOut) soldOut.hidden = available;
      }
      this.section.querySelectorAll('[data-add-to-cart], [data-add-to-cart-sticky]').forEach((button) => {
        button.disabled = !available;
        const label = button.querySelector('[data-atc-label]');
        if (label) label.textContent = available ? strings().addToCart : variant ? strings().soldOut : strings().unavailable;
      });
    }

    updateSummary(options) {
      if (!this.setConfig) return;
      const config = this.setConfig;
      const parts = Array.isArray(config.base) ? config.base.slice() : [];
      let notIncluded = config.not_included || '';
      options.forEach((value) => {
        if (!value) return;
        const extra = config.options && config.options[value];
        if (Array.isArray(extra)) parts.push(...extra);
        if (config.not_included_by_value && config.not_included_by_value[value]) notIncluded = config.not_included_by_value[value];
      });
      const list = this.section.querySelector('[data-included-list]');
      if (list) {
        list.innerHTML = parts
          .map(
            (part) =>
              `<li><svg class="icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5 9-10"></path></svg><span><b>${this.escape(part.name || '')}</b>${part.dims ? ` <span class="t-small">· ${this.escape(part.dims)}</span>` : ''}</span></li>`
          )
          .join('');
      }
      const countText = (strings().included || '[count] Teile').replace('[count]', parts.length);
      this.section.querySelectorAll('[data-included-count], [data-sticky-variant]').forEach((el) => {
        el.textContent = countText;
      });
      const note = this.section.querySelector('[data-not-included]');
      if (note) note.textContent = notIncluded;
    }

    escape(value) {
      return String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
    }
  }

  /* ------------------------------------------- set components state lines */

  document.addEventListener('ik:variant:change', (event) => {
    const { options, deltas } = event.detail || {};
    if (!options) return;
    const picker = event.target;
    const groups = picker.groups || [];
    document.querySelectorAll('[data-component-state]').forEach((el) => {
      const optionName = el.dataset.option;
      const index = groups.findIndex((group) => {
        const title = group.querySelector('.configurator__legend-title');
        return title && title.textContent.replace(/^\d+\s·\s/, '').trim() === optionName;
      });
      if (index < 0) return;
      const selected = options[index];
      const optional = el.dataset.optional === 'true';
      if (!optional) {
        el.textContent = `${strings().selected || 'Gewählt'}: ${selected}`;
        return;
      }
      const inputs = Array.from(groups[index].querySelectorAll('[data-option-input]'));
      const isBase = inputs.length && inputs[0].value === selected;
      if (!isBase) {
        el.textContent = el.dataset.chosenText || strings().chosen || 'Im Set gewählt';
        return;
      }
      const alternative = inputs[1];
      const info = alternative ? deltas[`${index}:${alternative.value}`] : null;
      if (info && !info.available) {
        el.textContent = el.dataset.soldoutText || strings().soldOut || '';
      } else if (info) {
        el.textContent = (el.dataset.notChosenText || 'Nicht gewählt · hinzufügen [delta]').replace('[delta]', info.text);
      }
    });
  });

  /* ------------------------------------------------ product recommendations */

  /* Loads Shopify's recommendations and drops products that are excluded (data-exclude),
     already shown by an earlier list on the page, or mattresses in a size the bed does not
     take (data-mattress-sizes vs. each item's sizes). Asks for a few extra products so the
     list stays full after filtering; lists re-filter when an earlier one finishes loading. */
  class IkRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
      this.fits = (this.dataset.mattressSizes || '').split(',').filter(Boolean);
      this.onListLoaded = () => this.render();
      document.addEventListener('ik:recommendations:loaded', this.onListLoaded);
      fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const grid = doc.querySelector('.related-products__grid');
          this.gridShell = grid ? grid.cloneNode(false) : null;
          this.items = grid ? Array.from(grid.children) : [];
          this.loaded = true;
          this.render();
          document.dispatchEvent(new CustomEvent('ik:recommendations:loaded'));
        })
        .catch(() => this.hideSection());
    }

    disconnectedCallback() {
      document.removeEventListener('ik:recommendations:loaded', this.onListLoaded);
    }

    hideSection() {
      const section = this.closest('.shopify-section');
      if (section) section.hidden = true;
    }

    shownEarlier() {
      const ids = new Set();
      document.querySelectorAll('[data-related-item]').forEach((el) => {
        if (this.contains(el)) return;
        if (el.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING) ids.add(el.dataset.productId);
      });
      return ids;
    }

    render() {
      if (!this.loaded) return;
      const taken = this.shownEarlier();
      const visible = this.items
        .filter((item) => {
          const id = item.dataset.productId;
          if (id && (this.exclude.has(id) || taken.has(id))) return false;
          const sizes = (item.dataset.mattressSizes || '').split(',').filter(Boolean);
          if (this.fits.length && sizes.length && !sizes.some((size) => this.fits.includes(size))) return false;
          return true;
        })
        .slice(0, this.limit);
      if (!this.gridShell || !visible.length) {
        this.replaceChildren();
        this.hideSection();
        return;
      }
      const section = this.closest('.shopify-section');
      if (section) section.hidden = false;
      const grid = this.gridShell.cloneNode(false);
      visible.forEach((item) => grid.appendChild(item.cloneNode(true)));
      this.replaceChildren(grid);
    }
  }

  /* Stars under the title jump to the review widget; the app decides the widget's id */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-rating-link]');
    if (!link) return;
    const target = document.querySelector('#judgeme_product_reviews, .jdgm-review-widget');
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  if (!customElements.get('ik-recommendations')) customElements.define('ik-recommendations', IkRecommendations);
  if (!customElements.get('ik-gallery')) customElements.define('ik-gallery', IkGallery);
  if (!customElements.get('ik-variant-picker')) customElements.define('ik-variant-picker', IkVariantPicker);
  if (!customElements.get('ik-product')) customElements.define('ik-product', class extends HTMLElement {});
})();
